export const NOTEBOOK_GENERATION_TIMEOUT_MS = 15 * 60 * 1000;
export const NOTEBOOK_STATUS_POLL_INTERVAL_MS = 20 * 1000;
export const NOTEBOOK_DONE_AUTO_CLOSE_MS = 1500;
export const NOTEBOOK_NOTIFICATION_POLL_COUNT = 20;

export const NOTIFICATION_CATEGORY_NOTEBOOK_PDF = 20;
export const NOTIFICATION_CATEGORY_NOTEBOOK_ZIP = 21;
export const NOTIFICATION_CATEGORY_APPLICATION_ERROR = 27;

export type NotebookFileType = 'PDF' | 'ZIP';

/** Responsive width shared by the download dialogs: 460px, or the viewport less a 16px gutter each side. */
export const DOWNLOAD_DIALOG_WIDTH = 'min(460px, calc(100vw - 32px))';


export type NotebookDownloadStep = 'requesting' | 'generating' | 'downloading' | 'done' | 'error';

export type NotebookDownloadErrorCode = 'REQUEST_FAILED' | 'GENERATION_FAILED' | 'TIMEOUT' | 'DOWNLOAD_FAILED';

export interface NotebookDownloadState {
  step: NotebookDownloadStep;
  requestId: string;
  notebookId: string;
  fileType: NotebookFileType;
  startedAt: number;
  generationId?: string;
  expectedBytes?: number;
  loadedBytes: number;
  totalBytes?: number;
  fileName?: string;
  errorCode?: NotebookDownloadErrorCode;
  serverErrorCode?: string;
  httpStatus?: number;
  failedStep?: NotebookDownloadStep;
}

export type NotebookDownloadEvent =
  | { type: 'requestAccepted'; requestId?: string }
  | { type: 'requestFailed'; httpStatus?: number }
  | { type: 'ready'; generationId: string; sizeBytes?: number }
  | { type: 'generationFailed'; serverErrorCode?: string }
  | { type: 'timedOut' }
  | { type: 'progress'; loadedBytes: number; totalBytes?: number }
  | { type: 'downloaded'; fileName: string; sizeBytes: number }
  | { type: 'downloadFailed'; httpStatus?: number };

export function initialNotebookDownloadState(requestId: string, notebookId: string, fileType: NotebookFileType, now: number): NotebookDownloadState {
  return { step: 'requesting', requestId, notebookId, fileType, startedAt: now, loadedBytes: 0 };
}

export function isAwaitingResult(state: NotebookDownloadState): boolean {
  return state.step === 'requesting' || state.step === 'generating' || (state.step === 'error' && state.errorCode === 'TIMEOUT');
}

export function isTerminal(state: NotebookDownloadState): boolean {
  return state.step === 'done' || (state.step === 'error' && state.errorCode !== 'TIMEOUT');
}

function fail(state: NotebookDownloadState, errorCode: NotebookDownloadErrorCode, extra: Partial<NotebookDownloadState> = {}): NotebookDownloadState {
  return { ...state, ...extra, step: 'error', errorCode, failedStep: state.step };
}

export function reduceNotebookDownload(state: NotebookDownloadState, event: NotebookDownloadEvent): NotebookDownloadState {
  if (isTerminal(state)) {
    return state;
  }

  switch (event.type) {
    case 'requestAccepted':
      return state.step === 'requesting' ? { ...state, step: 'generating', requestId: event.requestId || state.requestId } : state;

    case 'requestFailed':
      return state.step === 'requesting' ? fail(state, 'REQUEST_FAILED', { httpStatus: event.httpStatus }) : state;

    case 'ready':
      return isAwaitingResult(state)
        ? { ...state, step: 'downloading', generationId: event.generationId, expectedBytes: event.sizeBytes, errorCode: undefined, failedStep: undefined }
        : state;

    case 'generationFailed':
      return isAwaitingResult(state) ? fail({ ...state, step: state.step === 'error' ? 'generating' : state.step }, 'GENERATION_FAILED', { serverErrorCode: event.serverErrorCode }) : state;

    case 'timedOut':
      return state.step === 'requesting' || state.step === 'generating' ? fail(state, 'TIMEOUT') : state;

    case 'progress':
      return state.step === 'downloading' ? { ...state, loadedBytes: event.loadedBytes, totalBytes: event.totalBytes || state.expectedBytes } : state;

    case 'downloaded':
      return state.step === 'downloading' ? { ...state, step: 'done', loadedBytes: event.sizeBytes, fileName: event.fileName } : state;

    case 'downloadFailed':
      return state.step === 'downloading' ? fail(state, 'DOWNLOAD_FAILED', { httpStatus: event.httpStatus }) : state;

    default:
      return state;
  }
}

export function normalizeNotebookFileType(value: string | null | undefined): NotebookFileType | null {
  const upper = (value || '').replace('.', '').trim().toUpperCase();
  return upper === 'PDF' || upper === 'ZIP' ? upper : null;
}

function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

export interface NotebookResultTarget {
  requestId: string;
  notebookId: string;
  fileType: NotebookFileType;
}

export interface NotebookResultKey {
  requestId?: string | null;
  notebookId: string;
  fileType: string;
}

export type NotebookResultMatch = 'exact' | 'fallback' | 'none';

export function matchNotebookResult(target: NotebookResultTarget, result: NotebookResultKey): NotebookResultMatch {
  // The notebook must always be the one this dialog asked for, even when the request id matches,
  // so a result can never be relabelled onto a different notebook.
  if (!sameId(target.notebookId, result.notebookId)) {
    return 'none';
  }
  const resultFileType = normalizeNotebookFileType(result.fileType);
  if (result.requestId) {
    return sameId(target.requestId, result.requestId) && (!resultFileType || resultFileType === target.fileType) ? 'exact' : 'none';
  }
  return target.fileType === resultFileType ? 'fallback' : 'none';
}

export interface NotebookNotificationResult extends NotebookResultKey {
  kind: 'ready' | 'failed';
  notificationId: number;
  generationId?: string;
  sizeBytes?: number;
  serverErrorCode?: string;
}

interface NotificationLike {
  notificationID: number;
  categoryID: number;
  notificationMessage: string;
  relatedNodeID?: string;
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGITS_PATTERN = /^[0-9]{1,15}$/;
const ERROR_CODE_PATTERN = /^[A-Za-z0-9_]{1,64}$/;

function isGuid(value: string | null | undefined): boolean {
  return !!value && GUID_PATTERN.test(value);
}

// The attributes the server writes AFTER notebookName. Before the 2026-11-01 fix the title went into
// notebookName unencoded, so a title could add any of these ahead of the server's own copy, and
// DOMParser keeps only the first of a duplicated attribute. Each may appear at most once on the
// metadata span. Other duplicates (class, notebookname, stray words from a legitimate title such as
// "Jane's class") are harmless, because the server writes class and notebookName first and the
// first copy wins.
const SHADOWABLE_ATTRIBUTES = ['filesize', 'notebookid', 'requestid', 'filetype', 'errorcode'];

// Start tags that switch the HTML tokenizer into a text mode, hide content from querySelectorAll
// (template) or enter foreign content (CDATA). No server notification uses them, so a message that
// contains one is refused rather than modelled.
const UNSUPPORTED_TAGS = ['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript', 'plaintext', 'template', 'svg', 'math'];

interface RawStartTag {
  name: string;
  // Every attribute in source order, duplicates included. Names lower-cased as the parser does.
  attributes: { name: string; value: string }[];
  // Index just past the tag's closing '>'.
  end: number;
}

interface RawMarkup {
  tags: RawStartTag[];
  // False when the input ended inside a comment, tag or attribute value, i.e. the end of the
  // message (where the server writes its span) was swallowed by something earlier.
  complete: boolean;
}

const isHtmlSpace = (c: string) => c === ' ' || c === '\t' || c === '\n' || c === '\f' || c === '\r';
const isAsciiLetter = (c: string) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');

// Skips to just past the next '>' at or after i (bogus comment, doctype, processing instruction).
// Returns -1 at end of input.
function skipPastGreaterThan(html: string, i: number): number {
  const gt = html.indexOf('>', i);
  return gt < 0 ? -1 : gt + 1;
}

// The tag and attribute states of the WHATWG HTML tokenizer (13.2.5), enough to list every start
// tag with ALL of its attributes, which DOMParser cannot do because it drops duplicates. Character
// references are not decoded: values are only used to find the class, the real values come from
// the DOM. Returns null for markup outside the supported subset.
function tokenizeStartTags(html: string): RawMarkup | null {
  const tags: RawStartTag[] = [];
  const n = html.length;
  let i = 0;
  while (i < n) {
    const lt = html.indexOf('<', i);
    if (lt < 0) {
      break;
    }
    i = lt + 1;
    const c = html.charAt(i);
    if (c === '!') {
      if (html.startsWith('--', i + 1)) {
        // Comment. Ends at -->, --!>, or abruptly at <!--> and <!--->.
        const start = i + 3;
        if (html.charAt(start) === '>') {
          i = start + 1;
          continue;
        }
        if (html.startsWith('->', start)) {
          i = start + 2;
          continue;
        }
        const ends = [html.indexOf('-->', start), html.indexOf('--!>', start)].filter((x) => x >= 0);
        if (!ends.length) {
          return { tags, complete: false };
        }
        const end = Math.min(...ends);
        i = end + (html.startsWith('-->', end) ? 3 : 4);
        continue;
      }
      i = skipPastGreaterThan(html, i);
      if (i < 0) {
        return { tags, complete: false };
      }
      continue;
    }
    if (c === '?') {
      i = skipPastGreaterThan(html, i);
      if (i < 0) {
        return { tags, complete: false };
      }
      continue;
    }
    let isEndTag = false;
    if (c === '/') {
      const d = html.charAt(i + 1);
      if (d === '>') {
        i += 2;
        continue;
      }
      if (!isAsciiLetter(d)) {
        i = skipPastGreaterThan(html, i);
        if (i < 0) {
          return { tags, complete: false };
        }
        continue;
      }
      isEndTag = true;
      i++;
    } else if (!isAsciiLetter(c)) {
      continue; // A literal '<' in text.
    }

    // Tag name.
    let name = '';
    while (i < n && !isHtmlSpace(html.charAt(i)) && html.charAt(i) !== '/' && html.charAt(i) !== '>') {
      name += html.charAt(i++).toLowerCase();
    }
    const attributes: { name: string; value: string }[] = [];
    let closed = false;
    while (i < n) {
      const ch = html.charAt(i);
      // Before attribute name (also the self-closing slash, which is ignored on a span).
      if (isHtmlSpace(ch) || ch === '/') {
        i++;
        continue;
      }
      if (ch === '>') {
        i++;
        closed = true;
        break;
      }
      // Attribute name. A leading '=' is part of the name.
      let attrName = '';
      if (ch === '=') {
        attrName = '=';
        i++;
      }
      while (i < n && !isHtmlSpace(html.charAt(i)) && html.charAt(i) !== '/' && html.charAt(i) !== '>' && html.charAt(i) !== '=') {
        attrName += html.charAt(i++).toLowerCase();
      }
      // After attribute name.
      while (i < n && isHtmlSpace(html.charAt(i))) {
        i++;
      }
      let value = '';
      if (html.charAt(i) === '=') {
        i++;
        // Before attribute value.
        while (i < n && isHtmlSpace(html.charAt(i))) {
          i++;
        }
        const q = html.charAt(i);
        if (q === '"' || q === "'") {
          const close = html.indexOf(q, i + 1);
          if (close < 0) {
            return { tags, complete: false }; // End of input inside a value: the tag is never emitted.
          }
          value = html.substring(i + 1, close);
          i = close + 1;
        } else {
          while (i < n && !isHtmlSpace(html.charAt(i)) && html.charAt(i) !== '>') {
            value += html.charAt(i++);
          }
        }
      }
      attributes.push({ name: attrName, value });
    }
    if (!closed) {
      return { tags, complete: false }; // End of input inside a tag: the tag is never emitted.
    }
    if (!isEndTag) {
      if (UNSUPPORTED_TAGS.includes(name)) {
        return null;
      }
      tags.push({ name, attributes, end: i });
    }
  }
  return { tags, complete: true };
}

function isHidemeSpan(tag: RawStartTag): boolean {
  const cls = tag.attributes.find((a) => a.name === 'class');
  // DOMParser documents without a doctype are in quirks mode, where class selectors match
  // case-insensitively.
  return tag.name === 'span' && !!cls && cls.value.split(/[ \t\n\f\r]+/).some((t) => t.toLowerCase() === 'hideme');
}

const TRAILING_SPAN_END = /^<\/span\s*>\s*$/i;

// Reads the server's metadata span. Returns null unless:
// - the DOM and the raw markup each hold exactly one span.hideme;
// - that span is the LAST tag of the message and the message ends with its </span>. The server
//   always writes it there, and pre-fix rows repeat a raw title in the visible text, so a title
//   could otherwise plant a complete span earlier and comment out or swallow the real one;
// - none of its identity attributes is duplicated.
// The visible text is never trusted.
export function readNotebookNotificationAttributes(messageHtml: string): { [name: string]: string } | null {
  const html = messageHtml || '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const spans = doc.querySelectorAll('span.hideme');
  if (spans.length !== 1) {
    return null;
  }
  const raw = tokenizeStartTags(html);
  if (!raw || !raw.complete || !raw.tags.length) {
    return null;
  }
  const rawHideme = raw.tags.filter(isHidemeSpan);
  const last = raw.tags[raw.tags.length - 1];
  if (rawHideme.length !== 1 || rawHideme[0] !== last || !TRAILING_SPAN_END.test(html.substring(last.end))) {
    return null;
  }
  const names = rawHideme[0].attributes.map((a) => a.name);
  if (SHADOWABLE_ATTRIBUTES.some((name) => names.filter((x) => x === name).length > 1)) {
    return null;
  }
  const span = spans[0];
  const attributes: { [name: string]: string } = {};
  for (let i = 0; i < span.attributes.length; i++) {
    attributes[span.attributes[i].name.toLowerCase()] = span.attributes[i].value;
  }
  return attributes;
}

export function parseNotebookNotification(notification: NotificationLike): NotebookNotificationResult | null {
  const isReady = notification.categoryID === NOTIFICATION_CATEGORY_NOTEBOOK_PDF || notification.categoryID === NOTIFICATION_CATEGORY_NOTEBOOK_ZIP;
  const isFailure = notification.categoryID === NOTIFICATION_CATEGORY_APPLICATION_ERROR;
  if (!isReady && !isFailure) {
    return null;
  }
  const attributes = readNotebookNotificationAttributes(notification.notificationMessage);
  if (!attributes) {
    return null;
  }
  const notebookId = attributes['notebookid'];
  if (!isGuid(notebookId)) {
    return null;
  }
  const rawRequestId = attributes['requestid'] || '';
  if (rawRequestId && !isGuid(rawRequestId)) {
    return null;
  }
  const requestId = rawRequestId || null;

  if (isReady) {
    // relatedNodeID is a structured column the server sets to the generation id; the span cannot override it.
    if (!isGuid(notification.relatedNodeID)) {
      return null;
    }
    const rawSize = attributes['filesize'] || '';
    const sizeBytes = DIGITS_PATTERN.test(rawSize) ? Number(rawSize) : undefined;
    return {
      kind: 'ready',
      notificationId: notification.notificationID,
      notebookId,
      requestId,
      fileType: notification.categoryID === NOTIFICATION_CATEGORY_NOTEBOOK_PDF ? 'PDF' : 'ZIP',
      generationId: notification.relatedNodeID,
      sizeBytes,
    };
  }

  // Failure notifications store the notebook id in the structured relatedNodeID column. When it is
  // present it must agree with the span, and a failure is only ever matched by its request id.
  if (!requestId || (notification.relatedNodeID && !sameId(notification.relatedNodeID, notebookId))) {
    return null;
  }
  const fileType = normalizeNotebookFileType(attributes['filetype']);
  const errorCode = attributes['errorcode'];
  return {
    kind: 'failed',
    notificationId: notification.notificationID,
    notebookId,
    requestId,
    fileType: fileType || '',
    serverErrorCode: errorCode && ERROR_CODE_PATTERN.test(errorCode) ? errorCode : undefined,
  };
}

export function findNotebookNotificationResult(
  target: NotebookResultTarget,
  notifications: NotificationLike[],
  knownNotificationIds: Set<number> | null
): NotebookNotificationResult | null {
  for (const notification of notifications || []) {
    const result = parseNotebookNotification(notification);
    if (!result) {
      continue;
    }
    const match = matchNotebookResult(target, result);
    if (match === 'exact') {
      return result;
    }
    if (match === 'fallback' && result.kind === 'ready' && knownNotificationIds && !knownNotificationIds.has(result.notificationId)) {
      return result;
    }
  }
  return null;
}

export function describeNotebookDownloadForLog(state: NotebookDownloadState, now: number, detail: string): string {
  return [
    'NotebookDownload ' + detail,
    'requestId=' + state.requestId,
    'notebookId=' + state.notebookId,
    'fileType=' + state.fileType,
    'step=' + (state.failedStep || state.step),
    'elapsedMs=' + Math.max(0, now - state.startedAt),
    'httpStatus=' + (state.httpStatus === undefined ? 'none' : state.httpStatus),
    'errorCode=' + (state.errorCode || 'none'),
    'serverErrorCode=' + (state.serverErrorCode || 'none'),
  ].join(' ');
}

export function formatMegabytes(bytes: number | undefined): string {
  return ((bytes || 0) / (1024 * 1024)).toFixed(1);
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
}

export function formatFileNameDate(date: Date): string {
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + '-' + pad(date.getHours()) + pad(date.getMinutes());
}

export function newRequestId(random: (bytes: Uint8Array) => Uint8Array): string {
  const bytes = random(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => (b < 16 ? '0' : '') + b.toString(16)).join('');
  return hex.substring(0, 8) + '-' + hex.substring(8, 12) + '-' + hex.substring(12, 16) + '-' + hex.substring(16, 20) + '-' + hex.substring(20);
}
